import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { auth, db } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, sendPasswordResetEmail, UserCredential, User as FirebaseAuthUser } from 'firebase/auth';
import { doc, getDoc, onSnapshot, DocumentData } from 'firebase/firestore';
import { logAction, AUDIT_ACTIONS } from '../utils/auditLogger';
import { toast } from 'sonner';
import PageLoader from '../components/ui/PageLoader';
import { AuthContextType, FirebaseUser } from '../types';

interface RoleDefaultPermissions {
    [roleName: string]: string[];
}

interface AuthProviderProps {
    children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
    const [userName, setUserName] = useState<string | null>(null);
    const [userRole, setUserRole] = useState<string | null>(null);
    const [userPermissions, setUserPermissions] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState<boolean>(true);

    // Ref to track current user for logout logging without creating dependency cycles
    const currentUserRef = useRef<FirebaseUser | null>(null);
    useEffect(() => {
        currentUserRef.current = currentUser;
    }, [currentUser]);

    const signup = useCallback(async (email: string, password: string): Promise<UserCredential> => {
        return createUserWithEmailAndPassword(auth, email, password);
    }, []);

    const login = useCallback(async (email: string, password: string): Promise<UserCredential> => {
        const result = await signInWithEmailAndPassword(auth, email, password);
        const auditUser = { uid: result.user.uid, email: result.user.email ?? undefined };
        logAction(AUDIT_ACTIONS.LOGIN, 'auth', result.user.uid, auditUser, null, { email });
        return result;
    }, []);

    const logout = useCallback(async (): Promise<void> => {
        const user = currentUserRef.current;
        if (user) {
            const auditUser = { uid: user.uid, email: user.email ?? undefined };
            logAction(AUDIT_ACTIONS.LOGOUT, 'auth', user.uid, auditUser, null, { email: user.email });
        }
        return signOut(auth);
    }, []);

    const resetPassword = useCallback((email: string): Promise<void> => {
        return sendPasswordResetEmail(auth, email);
    }, []);

    // Default permissions for standard roles (fallback if not in DB)
    const ROLE_DEFAULT_PERMISSIONS: RoleDefaultPermissions = {
        'vendeur': [
            'dashboard_view',
            'inventory_view',
            'sales_view',
            'sales_manage',
            'customers_view',
            'customers_manage',
            'quotes_view',
            'repairs_view',
            'repairs_manage',
            'expenses_view',
            'expenses_manage'
        ],
        'manager': [
            'dashboard_view',
            'inventory_view', 'inventory_manage',
            'sales_view', 'sales_manage',
            'customers_view', 'customers_manage',
            'expenses_view', 'expenses_manage',
            'repairs_view', 'repairs_manage',
            'quotes_view'
        ],
        'accountant': [
            'dashboard_view',
            'sales_view',
            'expenses_view', 'expenses_manage'
        ],
        'stock': [
            'dashboard_view',
            'inventory_view',
            'inventory_manage',
            'sales_view',
            'customers_view',
            'expenses_view',
            'repairs_view',
            'quotes_view'
        ]
    };

    // Track the current role being listened to for permissions
    const [currentRoleName, setCurrentRoleName] = useState<string | null>(null);
    const [delegatedPermissions, setDelegatedPermissions] = useState<string[]>([]);

    function hasPermission(permissionId: string): boolean {
        if (userRole === 'admin') return true;

        // 1. Check delegated permissions (temporary grants)
        if (delegatedPermissions.includes(permissionId)) return true;

        // 2. Check explicit DB permissions
        if (userPermissions[permissionId]) return true;

        // 3. Check default role permissions (fallback)
        if (userRole && ROLE_DEFAULT_PERMISSIONS[userRole]?.includes(permissionId)) {
            return true;
        }

        // 4. Special case: Dashboard is always visible to authenticated users with a role
        if (permissionId === 'dashboard_view' && userRole) return true;

        return false;
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user: FirebaseAuthUser | null) => {
            if (user) {
                const firebaseUser: FirebaseUser = {
                    uid: user.uid,
                    email: user.email,
                    metadata: {
                        lastSignInTime: user.metadata.lastSignInTime || undefined
                    }
                };
                setCurrentUser(firebaseUser);

                // Fetch user data (role, name, etc.)
                const userDoc = await getDoc(doc(db, "users", user.uid));

                if (userDoc.exists()) {
                    const userData = userDoc.data() as DocumentData;
                    const roleName = userData.role as string;

                    // Map English role names to French (backwards compatibility)
                    const roleNameMap: Record<string, string> = {
                        'vendor': 'vendeur',
                        'salesperson': 'vendeur'
                    };
                    const mappedRoleName = roleNameMap[roleName] || roleName;

                    setUserRole(roleName);  // Keep original role for display
                    setUserName(userData.name || userData.nom || userData.email || user.email);
                    setCurrentRoleName(mappedRoleName); // Track role for permission listener
                } else {
                    // User document not found - set to invite role
                    setUserRole('invite');
                    setUserName(user.email);
                    setCurrentRoleName('invite');
                }
            } else {
                setCurrentUser(null);
                setUserRole(null);
                setUserName(null);
                setUserPermissions({});
                setCurrentRoleName(null);
                setDelegatedPermissions([]);
            }
            setLoading(false);
        });

        // Force loading to false after 5 seconds if firebase is slow
        const timeout = setTimeout(() => {
            setLoading((prevLoading: boolean) => {
                if (prevLoading) return false;
                return prevLoading;
            });
        }, 5000);

        return () => {
            unsubscribe();
            clearTimeout(timeout);
        };
    }, []);

    // Real-time listener for user document changes (role changes, deactivation, session revocation)
    useEffect(() => {
        if (!currentUser) return;

        const unsubscribeUser = onSnapshot(
            doc(db, "users", currentUser.uid),
            (userDoc) => {
                if (userDoc.exists()) {
                    const userData = userDoc.data() as DocumentData;

                    // Check for session revocation
                    if (userData.sessionRevokedAt) {
                        const revokedAt = userData.sessionRevokedAt.toDate?.() || new Date(userData.sessionRevokedAt);
                        // If session was revoked after current session started, force logout
                        if (currentUser.metadata.lastSignInTime && revokedAt > new Date(currentUser.metadata.lastSignInTime)) {
                            toast.error("Votre session a ete revoquee. Veuillez vous reconnecter.");
                            logout();
                            return;
                        }
                    }

                    // Check for account deactivation
                    if (userData.active === false) {
                        toast.error("Votre compte a ete desactive. Contactez un administrateur.");
                        logout();
                        return;
                    }

                    // Check for role expiration
                    if (userData.roleExpiresAt) {
                        const expiresAt = userData.roleExpiresAt.toDate?.() || new Date(userData.roleExpiresAt);
                        if (expiresAt < new Date()) {
                            // Role has expired - will be demoted to 'invite' by the system
                            // The actual demotion should be done via Cloud Function or admin action
                            // Here we just notify the user
                            if (userData.role !== 'invite') {
                                toast.warning("Votre role a expire. Contactez un administrateur.");
                            }
                        }
                    }

                    // Check and update delegated permissions
                    if (userData.delegatedPermissions && (userData.delegatedPermissions as string[]).length > 0) {
                        if (userData.delegationExpiresAt) {
                            const delegationExpires = userData.delegationExpiresAt.toDate?.() || new Date(userData.delegationExpiresAt);
                            if (delegationExpires > new Date()) {
                                // Delegated permissions are still valid
                                setDelegatedPermissions(userData.delegatedPermissions as string[]);
                            } else {
                                // Delegated permissions have expired
                                setDelegatedPermissions([]);
                            }
                        } else {
                            setDelegatedPermissions([]);
                        }
                    } else {
                        setDelegatedPermissions([]);
                    }

                    // Map role names (backwards compatibility)
                    const roleNameMap: Record<string, string> = {
                        'vendor': 'vendeur',
                        'salesperson': 'vendeur'
                    };
                    const newRole = userData.role as string;
                    const mappedRoleName = roleNameMap[newRole] || newRole;

                    // Update role if it changed (only show toast if role was already set)
                    if (newRole !== userRole) {
                        const wasAlreadySet = userRole !== null;
                        setUserRole(newRole);
                        setCurrentRoleName(mappedRoleName);
                        if (wasAlreadySet) {
                            toast.info("Votre role a ete mis a jour.");
                        }
                    }

                    // Update name if it changed
                    const newName = userData.name || userData.nom || userData.email;
                    if (newName !== userName) {
                        setUserName(newName as string);
                    }
                }
            },
            () => {
                // Error handled silently - UI will reflect state
            }
        );

        return () => {
            unsubscribeUser();
        };
    }, [currentUser, userRole, userName, logout]);

    // Real-time listener for role permissions
    useEffect(() => {
        if (!currentRoleName || userRole === 'admin') {
            // No role or admin (admin has all permissions)
            if (userRole === 'admin') {
                setUserPermissions({}); // Admin bypasses permissions
            }
            return;
        }

        // Set up real-time listener on the role document
        const unsubscribeRole = onSnapshot(
            doc(db, "roles", currentRoleName),
            (roleDoc) => {
                if (roleDoc.exists()) {
                    const permissions = (roleDoc.data().permissions || {}) as Record<string, boolean>;
                    setUserPermissions(permissions);
                } else {
                    setUserPermissions({});
                }
            },
            () => {
                setUserPermissions({});
            }
        );

        return () => {
            unsubscribeRole();
        };
    }, [currentRoleName, userRole]);

    // Auto-logout on inactivity (15 minutes - PCI-DSS compliance)
    useEffect(() => {
        if (!currentUser) {
            // No user logged in, skip idle timeout setup
            return undefined;
        }

        const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes (PCI-DSS recommended max for financial apps)
        let idleTimer: ReturnType<typeof setTimeout>;

        const resetTimer = (): void => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
                logout();
                toast.info("Vous avez ete deconnecte pour inactivite.");
            }, TIMEOUT_MS);
        };

        // Events to track activity
        const events: string[] = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

        // Initial start
        resetTimer();

        // Add listeners
        events.forEach(event => {
            window.addEventListener(event, resetTimer);
        });

        return () => {
            if (idleTimer) clearTimeout(idleTimer);
            events.forEach(event => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [currentUser, logout]);

    const value: AuthContextType = {
        currentUser,
        userName,
        userRole,
        userPermissions,
        delegatedPermissions,
        hasPermission,
        loading,
        signup,
        login,
        logout,
        resetPassword
    };

    if (loading) {
        return <PageLoader />;
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
